import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';

// PrimeNG Modules
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { ChipModule } from 'primeng/chip';

// Services
import { UserService } from '../../layout/service/user.service';
import { MessageService } from 'primeng/api';

// PDF Viewer
import { PdfViewerModule } from 'ng2-pdf-viewer';

@Component({
  selector: 'app-anatomi-details',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    ToastModule,
    ProgressSpinnerModule,
    DialogModule,
    TagModule,
    ChipModule,
    PdfViewerModule
  ],
  providers: [MessageService],
  template: `
    <div class="min-h-screen bg-gray-50 p-6">
      <div class="max-w-4xl mx-auto">
        <!-- Header -->
        <div class="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <div class="flex items-center gap-4 mb-4">
            <p-button
              icon="pi pi-arrow-left"
              severity="secondary"
              [text]="true"
              size="large"
              (onClick)="goBack()"
            ></p-button>
            <div class="flex-1">
              <h1 class="text-2xl font-bold text-gray-900">
                {{currentAnatomy?.name || currentAnatomy?.anatomy_name || 'Pembelajaran Anatomi'}}
              </h1>
              <p class="text-gray-600 mt-1">{{currentAnatomy?.description || currentAnatomy?.anatomy_description || 'Pelajari anatomi melalui soal interaktif'}}</p>
            </div>
            <div class="bg-blue-50 px-4 py-2 rounded-lg">
              <div class="text-sm text-blue-600 font-medium">{{currentQuestions.length}} Soal</div>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div *ngIf="loading" class="flex flex-col items-center justify-center py-20">
          <p-progressSpinner [style]="{ width: '50px', height: '50px' }"></p-progressSpinner>
          <p class="text-gray-500 mt-4">Memuat pembelajaran...</p>
        </div>

        <!-- Learning Content -->
        <div *ngIf="!loading && currentQuestions.length > 0" class="space-y-6">
          <!-- Progress Bar -->
          <div class="bg-white rounded-xl shadow-sm border p-6">
            <div class="flex justify-between items-center mb-4">
              <div>
                <div class="text-sm text-gray-600">Soal {{currentQuestionIndex + 1}} dari {{currentQuestions.length}}</div>
                <div class="text-2xl font-bold text-gray-900">{{getProgressPercentage()}}% Selesai</div>
              </div>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div
                class="bg-blue-600 h-2 rounded-full transition-all duration-300"
                [style.width.%]="getProgressPercentage()"
              ></div>
            </div>
          </div>

          <!-- Question Content -->
          <div *ngIf="currentQuestion" class="space-y-6">
            <!-- Question Image -->
            <div *ngIf="currentQuestion.imagePreview" class="bg-white rounded-xl shadow-sm border p-6">
              <div class="text-center">
                <img
                  [src]="currentQuestion.imagePreview"
                  alt="Gambar soal"
                  class="max-w-full max-h-80 mx-auto rounded-lg shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                  (click)="viewImage(currentQuestion.imagePreview)"
                />
              </div>
            </div>

            <!-- Question Text -->
            <div class="bg-white rounded-xl shadow-sm border p-6">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">Pertanyaan</h3>
              <div [innerHTML]="currentQuestion.question" class="text-gray-700 leading-relaxed"></div>
            </div>

            <!-- Answer Input -->
            <div class="bg-white rounded-xl shadow-sm border p-6 space-y-6">
              <!-- Pesan jika sudah completed -->
              <div *ngIf="isCompleted" class="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div class="flex items-center">
                  <i class="pi pi-check-circle text-green-500 text-xl mr-3"></i>
                  <div class="flex-1">
                    <h4 class="font-semibold text-green-800">Pembelajaran Sudah Selesai</h4>
                    <p class="text-green-700 text-sm">Anda sudah menyelesaikan pembelajaran ini dengan skor {{finalAverageScore}}%. Input tidak dapat diubah lagi.</p>
                    <div *ngIf="completionData?.submittedToServer" class="mt-2">
                      <span class="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                        <i class="pi pi-cloud-upload mr-1"></i>
                        Tersimpan di server
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Jawaban Anda -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Jawaban Anda</label>
                <input
                  type="text"
                  [(ngModel)]="userAnswers[currentQuestionIndex]"
                  placeholder="Masukkan jawaban singkat..."
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  [disabled]="isCompleted"
                  [class]="isCompleted ? 'bg-gray-100 cursor-not-allowed' : ''"
                  (ngModelChange)="saveData()"
                />
              </div>

              <!-- Jelaskan Fungsi -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Jelaskan Fungsi</label>
                <textarea
                  [(ngModel)]="userExplanations[currentQuestionIndex]"
                  rows="4"
                  placeholder="Jelaskan fungsi atau detail tambahan..."
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none transition-colors"
                  [disabled]="isCompleted"
                  [class]="isCompleted ? 'bg-gray-100 cursor-not-allowed' : ''"
                  (ngModelChange)="saveData()"
                ></textarea>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="bg-white rounded-xl shadow-sm border p-6">
              <div class="flex justify-between items-center">
                <p-button
                  label="Sebelumnya"
                  icon="pi pi-chevron-left"
                  severity="secondary"
                  [outlined]="true"
                  (onClick)="previousQuestion()"
                  [disabled]="currentQuestionIndex === 0"
                ></p-button>                <div class="flex gap-3">
                  <p-button
                    *ngIf="isLearningFinished"
                    label="Semua PDF di bawah"
                    icon="pi pi-arrow-down"
                    severity="success"
                    [outlined]="true"
                    disabled="true"
                  ></p-button>
                </div>

                <p-button
                  *ngIf="currentQuestionIndex < currentQuestions.length - 1"
                  label="Selanjutnya"
                  icon="pi pi-chevron-right"
                  iconPos="right"
                  severity="primary"
                  (onClick)="nextQuestion()"
                ></p-button>

                <p-button
                  *ngIf="currentQuestionIndex === currentQuestions.length - 1 && !isLearningFinished"
                  label="Selesai"
                  icon="pi pi-check"
                  severity="success"
                  (onClick)="finishLearning()"
                ></p-button>

                <p-button
                  *ngIf="currentQuestionIndex === currentQuestions.length - 1 && isLearningFinished"
                  label="Lihat Hasil"
                  icon="pi pi-chart-bar"
                  severity="info"
                  (onClick)="showResultsDialog = true"
                ></p-button>
              </div>
            </div>

            <!-- PDF Preview after completion -->
            <div *ngIf="isLearningFinished && currentQuestion.pdfPreview" class="bg-white rounded-xl shadow-sm border p-6">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">📄 Materi PDF - Soal {{currentQuestionIndex + 1}}</h3>
              <div class="border rounded-lg overflow-hidden">
                <pdf-viewer
                  [src]="getPdfSrc(currentQuestion.pdfPreview)"
                  [render-text]="true"
                  [original-size]="false"
                  [fit-to-page]="true"
                  [show-all]="true"
                  [page]="1"
                  [zoom]="0.8"
                  [rotation]="0"
                  [show-borders]="true"
                  [loading]="'Memuat PDF...'"
                  [error]="'Error memuat PDF'"
                  style="display: block; width: 100%; height: 600px; border: none;"
                  (after-load-complete)="onPdfLoadComplete($event)"
                  (error)="onPdfError($event)"
                ></pdf-viewer>
              </div>
            </div>

            <!-- Semua PDF Preview setelah selesai -->
            <div *ngIf="isLearningFinished" class="space-y-6">
              <!-- Header untuk semua PDF -->
              <div class="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-sm border p-6">
                <div class="text-center">
                  <h2 class="text-2xl font-bold text-gray-900 mb-2">📚 Semua Materi PDF</h2>
                  <p class="text-gray-600">Berikut adalah semua materi PDF untuk setiap soal yang telah Anda kerjakan</p>
                  <div class="mt-4 inline-flex items-center bg-white px-4 py-2 rounded-lg shadow-sm">
                    <i class="pi pi-file-pdf text-red-500 mr-2"></i>
                    <span class="text-sm font-medium text-gray-700">{{getPdfCount()}} PDF tersedia</span>
                  </div>
                </div>
              </div>

              <!-- PDF untuk setiap soal -->
              <div *ngFor="let question of currentQuestions; let i = index" class="bg-white rounded-xl shadow-sm border">
                <div *ngIf="question.pdfPreview" class="p-6 space-y-4">
                  <div class="flex items-center justify-between border-b pb-3">
                    <div class="flex items-center space-x-3">
                      <div class="bg-blue-100 text-blue-800 font-bold text-sm px-3 py-1 rounded-full">
                        Soal {{i + 1}}
                      </div>
                      <h3 class="text-lg font-semibold text-gray-900">📄 Materi PDF</h3>
                    </div>
                    <div class="bg-gray-50 px-3 py-1 rounded-lg max-w-md">
                      <span class="text-sm text-gray-600">{{stripHtml(question.question).substring(0, 60)}}{{stripHtml(question.question).length > 60 ? '...' : ''}}</span>
                    </div>
                  </div>
                  <div class="border rounded-lg overflow-hidden shadow-inner">
                    <pdf-viewer
                      [src]="getPdfSrc(question.pdfPreview)"
                      [render-text]="true"
                      [original-size]="false"
                      [fit-to-page]="true"
                      [show-all]="true"
                      [page]="1"
                      [zoom]="0.75"
                      [rotation]="0"
                      [show-borders]="true"
                      [loading]="'Memuat PDF soal ' + (i + 1) + '...'"
                      [error]="'Error memuat PDF soal ' + (i + 1)"
                      style="display: block; width: 100%; height: 500px; border: none;"
                      (after-load-complete)="onPdfLoadComplete($event)"
                      (error)="onPdfError($event)"
                    ></pdf-viewer>
                  </div>
                </div>

                <!-- Jika tidak ada PDF untuk soal ini -->
                <div *ngIf="!question.pdfPreview" class="p-6">
                  <div class="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                    <i class="pi pi-file text-gray-300 text-4xl mb-3 block"></i>
                    <h4 class="text-lg font-medium text-gray-500">Soal {{i + 1}}</h4>
                    <p class="text-gray-400 text-sm">Tidak ada PDF tersedia untuk soal ini</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div *ngIf="!loading && currentQuestions.length === 0" class="text-center py-20">
          <div class="bg-white rounded-xl shadow-sm border p-12 max-w-lg mx-auto">
            <i class="pi pi-exclamation-triangle text-6xl text-gray-300 mb-6 block"></i>
            <h3 class="text-xl font-semibold text-gray-700 mb-3">Tidak Ada Konten</h3>
            <p class="text-gray-500 mb-6">Materi pembelajaran belum tersedia.</p>
            <p-button
              label="Kembali"
              icon="pi pi-arrow-left"
              severity="info"
              (onClick)="goBack()"
            ></p-button>
          </div>
        </div>
      </div>

      <!-- Toast Messages -->
      <p-toast></p-toast>

      <!-- Answer Dialog -->
      <p-dialog
        header="Kunci Jawaban"
        [(visible)]="showAnswerDialog"
        [modal]="true"
        [style]="{ width: '600px' }"
        [closable]="true"
      >
        <div *ngIf="currentQuestion" class="space-y-4">
          <div class="p-4 bg-green-50 border border-green-200 rounded-lg">
            <h4 class="font-semibold text-green-800 mb-2">Jawaban yang Benar</h4>
            <p class="text-green-700">{{currentQuestion.answerKey}}</p>
          </div>

          <div *ngIf="userAnswers[currentQuestionIndex]" class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 class="font-semibold text-blue-800 mb-2">Jawaban Anda</h4>
            <p class="text-blue-700">{{userAnswers[currentQuestionIndex]}}</p>
          </div>

          <div *ngIf="userExplanations[currentQuestionIndex]" class="p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <h4 class="font-semibold text-purple-800 mb-2">Penjelasan Fungsi Anda</h4>
            <p class="text-purple-700">{{userExplanations[currentQuestionIndex]}}</p>
          </div>
        </div>
      </p-dialog>

      <!-- Results Dialog -->
      <p-dialog
        header="📊 Hasil Pembelajaran"
        [(visible)]="showResultsDialog"
        [modal]="true"
        [style]="{ width: '90vw', maxWidth: '1000px', height: '85vh' }"
        [maximizable]="true"
        [closable]="true"
      >
        <div class="h-full flex flex-col space-y-4">
          <!-- Overall Score -->
          <div class="p-4 bg-blue-50 border border-blue-200 rounded-lg text-center flex-shrink-0">
            <h3 class="text-2xl font-bold text-blue-800 mb-2">Skor Rata-rata</h3>
            <div class="text-4xl font-bold text-blue-600">{{finalAverageScore}}%</div>
            <p class="text-blue-700 mt-2">
              {{finalAverageScore >= 80 ? '🎉 Luar Biasa!' : finalAverageScore >= 60 ? '👍 Bagus!' : '💪 Perlu Latihan Lagi'}}
            </p>
          </div>

          <!-- Content Area with Tabs -->
          <div class="flex-1 flex flex-col min-h-0">
            <!-- Tab Navigation -->
            <div class="flex border-b mb-4 flex-shrink-0">
              <button
                class="px-4 py-2 font-medium text-sm border-b-2 transition-colors"
                [class]="activeResultTab === 'scores' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'"
                (click)="activeResultTab = 'scores'"
              >
                📊 Detail Skor
              </button>
              <button
                *ngIf="availablePdfs.length > 0"
                class="px-4 py-2 font-medium text-sm border-b-2 transition-colors ml-4"
                [class]="activeResultTab === 'pdfs' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'"
                (click)="activeResultTab = 'pdfs'"
              >
                📄 Materi PDF ({{availablePdfs.length}})
              </button>
            </div>

            <!-- Scores Tab Content -->
            <div *ngIf="activeResultTab === 'scores'" class="flex-1 overflow-y-auto">
              <div class="space-y-3 pr-2">
                <h4 class="font-semibold text-gray-800 mb-3">Detail Skor per Soal:</h4>
                <div *ngFor="let question of currentQuestions; let i = index" class="border rounded-lg p-3">
                  <div class="flex justify-between items-start mb-2">
                    <span class="font-medium text-gray-700">Soal {{i + 1}}</span>
                    <span class="text-lg font-bold"
                          [class]="finalScores[i] >= 80 ? 'text-green-600' : finalScores[i] >= 60 ? 'text-yellow-600' : 'text-red-600'">
                      {{finalScores[i]}}%
                    </span>
                  </div>

                  <div *ngIf="userAnswers[i]?.trim()" class="text-sm space-y-1">
                    <div>
                      <strong class="text-gray-600">Jawaban Anda:</strong>
                      <span class="text-gray-800">{{userAnswers[i]}}</span>
                    </div>
                    <div>
                      <strong class="text-gray-600">Jawaban Benar:</strong>
                      <span class="text-gray-800">{{question.answerKey || 'Tidak tersedia'}}</span>
                    </div>
                  </div>

                  <div *ngIf="!userAnswers[i]?.trim()" class="text-sm text-gray-500">
                    Tidak dijawab
                  </div>

                  <!-- PDF button for each question -->
                  <div *ngIf="question.pdfPreview" class="mt-2">
                    <p-button
                      label="Lihat PDF Soal {{i + 1}}"
                      icon="pi pi-file-pdf"
                      severity="danger"
                      size="small"
                      [outlined]="true"
                      (onClick)="viewResultPdf(question.pdfPreview, i + 1)"
                    ></p-button>
                  </div>
                </div>
              </div>
            </div>

            <!-- PDFs Tab Content -->
            <div *ngIf="activeResultTab === 'pdfs' && availablePdfs.length > 0" class="flex-1 flex flex-col min-h-0">
              <!-- PDF Selection -->
              <div class="mb-4 flex-shrink-0">
                <label class="block text-sm font-medium text-gray-700 mb-2">Pilih PDF untuk ditampilkan:</label>
                <select
                  [(ngModel)]="selectedResultPdfIndex"
                  (ngModelChange)="onResultPdfChange()"
                  class="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option *ngFor="let pdf of availablePdfs; let i = index" [value]="i">
                    PDF Soal {{pdf.questionNumber}} - {{pdf.questionText | slice:0:50}}{{pdf.questionText.length > 50 ? '...' : ''}}
                  </option>
                </select>
              </div>

              <!-- PDF Viewer -->
              <div *ngIf="selectedResultPdf" class="flex-1 min-h-0">
                <pdf-viewer
                  [src]="selectedResultPdf"
                  [render-text]="true"
                  [original-size]="false"
                  [fit-to-page]="true"
                  [show-all]="true"
                  [page]="1"
                  [zoom]="1.0"
                  [rotation]="0"
                  [show-borders]="true"
                  [loading]="'Memuat PDF...'"
                  [error]="'Error memuat PDF'"
                  style="display: block; width: 100%; height: 100%; border: 1px solid #ccc; border-radius: 8px;"
                  (after-load-complete)="onResultPdfLoadComplete($event)"
                  (error)="onResultPdfError($event)"
                ></pdf-viewer>
              </div>
            </div>
          </div>

          <!-- Action Button -->
          <div class="text-center pt-4 flex-shrink-0">
            <p-button
              label="Kembali ke Daftar"
              icon="pi pi-arrow-left"
              severity="primary"
              (onClick)="goBack()"
            ></p-button>
          </div>
        </div>
      </p-dialog>

      <!-- Image Preview Dialog -->
      <p-dialog
        header="Preview Gambar"
        [(visible)]="showImageDialog"
        [modal]="true"
        [style]="{ width: '80vw', maxWidth: '800px' }"
        [closable]="true"
      >
        <div *ngIf="selectedImage" class="text-center">
          <img
            [src]="selectedImage"
            alt="Preview gambar"
            class="max-w-full h-auto rounded-lg"
          />
        </div>
      </p-dialog>

      <!-- PDF Viewer Dialog -->
      <p-dialog
        header="Viewer PDF"
        [(visible)]="showPdfDialog"
        [modal]="true"
        [style]="{ width: '90vw', height: '85vh' }"
        [maximizable]="true"
        [closable]="true"
        (onHide)="closePdfDialog()"
      >
        <div *ngIf="selectedPdf" class="h-full">
          <pdf-viewer
            [src]="selectedPdf"
            [render-text]="true"
            [original-size]="false"
            [fit-to-page]="true"
            [show-all]="true"
            [page]="1"
            [zoom]="1.0"
            [rotation]="0"
            [show-borders]="true"
            [loading]="'Memuat PDF...'"
            [error]="'Error memuat PDF'"
            style="display: block; width: 100%; height: 75vh; border: 1px solid #ccc; border-radius: 8px;"
            (after-load-complete)="onPdfLoadComplete($event)"
            (error)="onPdfError($event)"
          ></pdf-viewer>
        </div>
      </p-dialog>
    </div>
  `
})
export class AnatomyDetails implements OnInit {
  anatomyId!: number;
  loading: boolean = false;
  currentAnatomy: any = null;
  currentQuestions: any[] = [];
  currentQuestionIndex: number = 0;
  currentQuestion: any = null;
  userAnswers: string[] = [];
  userExplanations: string[] = [];
  isLearningFinished: boolean = false;
  isCompleted: boolean = false; // Sudah pernah dinilai
  completionData: any = null; // Data hasil completion

  // Dialog states
  showAnswerDialog: boolean = false;
  showImageDialog: boolean = false;
  showPdfDialog: boolean = false;
  showResultsDialog: boolean = false;
  activeResultTab = 'scores'; // 'scores' or 'pdfs'

  // PDF management for results
  availablePdfs: { questionNumber: number; questionText: string; pdfPath: string }[] = [];
  selectedResultPdfIndex = 0;
  selectedResultPdf: string | null = null;

  selectedImage: string | null = null;
  selectedPdf: any = null;
  finalScores: number[] = [];
  finalAverageScore: number = 0;

  constructor(
    private userService: UserService,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    // Get anatomy ID from route params
    this.route.params.subscribe(params => {
      this.anatomyId = +params['id'];
      if (this.anatomyId) {
        // Load saved data first
        this.loadSavedData();
        this.loadAnatomyDetails();
      } else {
        console.error('No anatomy ID found in route params');
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'ID anatomi tidak ditemukan'
        });
        this.goBack();
      }
    });
  }

  loadSavedData() {
    try {
      const savedData = localStorage.getItem(`anatomy_${this.anatomyId}`);
      if (savedData) {
        const data = JSON.parse(savedData);
        this.userAnswers = data.userAnswers || [];
        this.userExplanations = data.userExplanations || [];
        this.isLearningFinished = data.isLearningFinished || false;
        this.isCompleted = data.isCompleted || false;
        this.completionData = data.completionData || null;
        this.finalScores = data.finalScores || [];
        this.finalAverageScore = data.finalAverageScore || 0;
        // Restore current question index if available
        this.currentQuestionIndex = data.currentQuestionIndex || 0;
      }
    } catch (error) {
      console.error('Error loading saved data:', error);
      this.messageService.add({
        severity: 'warn',
        summary: 'Peringatan',
        detail: 'Gagal memuat data tersimpan. Memulai dari awal.'
      });
    }
  }

  saveData() {
    try {
      const dataToSave = {
        userAnswers: this.userAnswers,
        userExplanations: this.userExplanations,
        isLearningFinished: this.isLearningFinished,
        isCompleted: this.isCompleted,
        completionData: this.completionData,
        finalScores: this.finalScores,
        finalAverageScore: this.finalAverageScore,
        currentQuestionIndex: this.currentQuestionIndex,
        timestamp: new Date().toISOString()
      };

      localStorage.setItem(`anatomy_${this.anatomyId}`, JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Error saving data to localStorage:', error);
      this.messageService.add({
        severity: 'warn',
        summary: 'Peringatan',
        detail: 'Gagal menyimpan progress. Data mungkin hilang saat refresh.'
      });
    }
  }

  async loadAnatomyDetails() {
    this.loading = true;
    try {
      // Load anatomy basic info first
      const listResponse = await this.userService.getAnatomy({
        page: 1,
        limit: 100
      });

      if (listResponse && listResponse.status && listResponse.data) {
        this.currentAnatomy = listResponse.data.find((item: any) =>
          (item.id == this.anatomyId) || (item.anatomy_id == this.anatomyId)
        );
      }

      // Load anatomy details
      const response = await this.userService.getAnatomyDetails({
        anatomy_id: this.anatomyId
      });

      if (response && response.status) {
        // Don't reset these if we have saved data
        const hadSavedData = this.userAnswers.length > 0 || this.isCompleted;

        if (!hadSavedData) {
          this.currentQuestions = [];
          this.currentQuestionIndex = 0;
          this.userAnswers = [];
          this.userExplanations = [];
        }

        // Process the response data
        const anatomyData = response.data;
        let contentDetails = [];

        if (Array.isArray(anatomyData)) {
          contentDetails = anatomyData;
        } else if (anatomyData && anatomyData.details) {
          contentDetails = anatomyData.details;
        } else if (anatomyData && anatomyData.contents) {
          contentDetails = anatomyData.contents;
        }

        // Map content to questions
        if (contentDetails && contentDetails.length > 0) {
          // Map content to questions
          this.currentQuestions = contentDetails.map((content: any) => ({
            id: content.id || content.detail_id || '',
            question: content.question || content.content || content.pertanyaan || '',
            answerKey: content.key_answer || content.answer_key || content.kunci_jawaban || '',
            image: content.image || content.gambar || '',
            pdf: content.pdf || content.file_pdf || content.explanation || '',
            imagePreview: this.getImageSrc(content.image || content.gambar),
            pdfPreview: this.getPdfPreview(content.explanation || content.pdf || content.file_pdf)
          }));

          // Initialize user answers and explanations arrays only if not loaded from localStorage
          if (this.userAnswers.length === 0) {
            this.userAnswers = new Array(this.currentQuestions.length).fill('');
          } else {
            // Ensure arrays are the right length if questions changed
            while (this.userAnswers.length < this.currentQuestions.length) {
              this.userAnswers.push('');
            }
          }

          if (this.userExplanations.length === 0) {
            this.userExplanations = new Array(this.currentQuestions.length).fill('');
          } else {
            // Ensure arrays are the right length if questions changed
            while (this.userExplanations.length < this.currentQuestions.length) {
              this.userExplanations.push('');
            }
          }

          // Set current question based on saved index or default to first question
          if (this.currentQuestionIndex >= 0 && this.currentQuestionIndex < this.currentQuestions.length) {
            this.currentQuestion = this.currentQuestions[this.currentQuestionIndex];
          } else {
            this.currentQuestionIndex = 0;
            this.currentQuestion = this.currentQuestions[0];
          }

          // Check if already completed
          if (this.isCompleted) {
            this.messageService.add({
              severity: 'info',
              summary: 'Pembelajaran Sudah Selesai',
              detail: `Anda sudah menyelesaikan pembelajaran ini dengan skor ${this.finalAverageScore}%`
            });
            // Prepare PDFs for viewing
            this.prepareResultPdfs();
          } else {
            this.messageService.add({
              severity: 'success',
              summary: 'Berhasil',
              detail: `Memulai pembelajaran: ${this.currentAnatomy?.name || this.currentAnatomy?.anatomy_name}`
            });
          }
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: 'Peringatan',
            detail: 'Tidak ada konten pembelajaran yang tersedia untuk anatomi ini'
          });
        }
      } else {
        throw new Error('Gagal memuat detail anatomi');
      }
    } catch (error) {
      console.error('Error loading anatomy details:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Gagal memuat detail anatomi'
      });
    } finally {
      this.loading = false;
    }
  }

  nextQuestion() {
    if (this.currentQuestionIndex < this.currentQuestions.length - 1) {
      this.currentQuestionIndex++;
      this.currentQuestion = this.currentQuestions[this.currentQuestionIndex];
      this.saveData(); // Save progress when navigating
    }
  }

  previousQuestion() {
    if (this.currentQuestionIndex > 0) {
      this.currentQuestionIndex--;
      this.currentQuestion = this.currentQuestions[this.currentQuestionIndex];
      this.saveData(); // Save progress when navigating
    }
  }

  showAnswer() {
    this.showAnswerDialog = true;
  }

  async finishLearning() {
    // Prevent finishing if already completed
    if (this.isCompleted) {
      this.messageService.add({
        severity: 'info',
        summary: 'Informasi',
        detail: 'Pembelajaran sudah selesai sebelumnya.'
      });
      return;
    }

    const answeredCount = this.userAnswers.filter(answer => answer.trim()).length;
    const explainedCount = this.userExplanations.filter(explanation => explanation.trim()).length;

    // Calculate scores for each answer
    const scores = this.calculateScores();
    const totalScore = scores.reduce((sum, score) => sum + score, 0);
    const averageScore = answeredCount > 0 ? Math.round(totalScore / answeredCount) : 0;

    // Submit answers to API
    await this.submitAnatomyAnswers(scores, averageScore);

    // Show detailed results
    this.showResults(scores, answeredCount, explainedCount, averageScore);

    // Don't auto-navigate since we're showing results dialog
  }

  calculateScores(): number[] {
    return this.currentQuestions.map((question, index) => {
      const userAnswer = this.userAnswers[index]?.trim().toLowerCase() || '';
      const correctAnswer = question.answerKey?.trim().toLowerCase() || '';

      if (!userAnswer || !correctAnswer) {
        return 0;
      }

      // Calculate character matching score
      const maxLength = Math.max(userAnswer.length, correctAnswer.length);
      let matchingCharacters = 0;

      for (let i = 0; i < Math.min(userAnswer.length, correctAnswer.length); i++) {
        if (userAnswer[i] === correctAnswer[i]) {
          matchingCharacters++;
        }
      }

      // Calculate percentage score
      const score = Math.round((matchingCharacters / maxLength) * 100);
      return Math.max(0, Math.min(100, score)); // Ensure score is between 0-100
    });
  }

  showResults(scores: number[], answeredCount: number, explainedCount: number, averageScore: number) {
    // Store results for dialog
    this.finalScores = scores;
    this.finalAverageScore = averageScore;
    this.isLearningFinished = true;
    this.isCompleted = true; // Mark as completed to prevent re-answering

    // Save completion data
    this.completionData = {
      scores: scores,
      averageScore: averageScore,
      answeredCount: answeredCount,
      explainedCount: explainedCount,
      completedAt: new Date().toISOString()
    };

    // Save all data to localStorage
    this.saveData();

    // Prepare available PDFs for results dialog
    this.prepareResultPdfs();

    // Show summary message
    this.messageService.add({
      severity: 'success',
      summary: 'Pembelajaran Selesai! 🎉',
      detail: `Skor Rata-rata: ${averageScore}% | Dijawab: ${answeredCount}/${this.currentQuestions.length} | Penjelasan: ${explainedCount}`,
      life: 5000
    });

    // Scroll to PDF section after a short delay to allow UI to update
    setTimeout(() => {
      this.scrollToPdfSection();
    }, 1000);

    // Don't auto-show results dialog, wait for user to click "Lihat Hasil"
  }

  scrollToPdfSection() {
    // Try to find the first PDF section and scroll to it
    const pdfElements = document.querySelectorAll('pdf-viewer');
    if (pdfElements.length > 0) {
      const firstPdfElement = pdfElements[0] as HTMLElement;
      const pdfContainer = firstPdfElement.closest('.bg-white') as HTMLElement;
      if (pdfContainer) {
        pdfContainer.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  }

  getPdfCount(): number {
    return this.currentQuestions.filter(q => q.pdfPreview).length;
  }

  prepareResultPdfs() {
    this.availablePdfs = [];
    this.currentQuestions.forEach((question, index) => {
      if (question.pdfPreview) {
        this.availablePdfs.push({
          questionNumber: index + 1,
          questionText: this.stripHtml(question.question || 'Soal tanpa teks'),
          pdfPath: question.pdfPreview
        });
      }
    });

    // Set the first PDF as selected if available
    if (this.availablePdfs.length > 0) {
      this.selectedResultPdfIndex = 0;
      this.setSelectedResultPdf();
    }
  }

  stripHtml(html: string): string {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  setSelectedResultPdf() {
    if (this.availablePdfs.length > 0 && this.selectedResultPdfIndex >= 0 && this.selectedResultPdfIndex < this.availablePdfs.length) {
      const pdfData = this.availablePdfs[this.selectedResultPdfIndex].pdfPath;
      try {
        // Use the same logic as getPdfSrc
        this.selectedResultPdf = this.getPdfSrc(pdfData);
      } catch (error) {
        console.error('Error setting result PDF:', error);
        this.selectedResultPdf = null;
        this.messageService.add({
          severity: 'warn',
          summary: 'Peringatan PDF',
          detail: 'Format PDF tidak valid'
        });
      }
    } else {
      this.selectedResultPdf = null;
    }
  }

  onResultPdfChange() {
    this.setSelectedResultPdf();
  }

  viewResultPdf(pdfData: string, questionNumber: number) {
    // Switch to PDFs tab and select the specific PDF
    const pdfIndex = this.availablePdfs.findIndex(pdf => pdf.questionNumber === questionNumber);
    if (pdfIndex >= 0) {
      this.activeResultTab = 'pdfs';
      this.selectedResultPdfIndex = pdfIndex;
      this.setSelectedResultPdf();
    }
  }

  onResultPdfLoadComplete(event: any) {
    // Result PDF loaded successfully
  }

  onResultPdfError(error: any) {
    this.messageService.add({
      severity: 'error',
      summary: 'Error PDF',
      detail: 'Gagal memuat file PDF hasil'
    });
  }

  async submitAnatomyAnswers(scores: number[], averageScore: number) {
    try {
      // Check if already submitted to avoid duplicate submissions
      if (this.completionData && this.completionData.submittedToServer) {
        return;
      }

      // Get user ID from localStorage
      const userId = this.getUserId();
      if (!userId) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Peringatan',
          detail: 'Data pengguna tidak ditemukan. Hasil tidak dapat disimpan ke server.'
        });
        return;
      }

      // Prepare the details array
      const details = this.currentQuestions.map((question, index) => {        const anatomyDetailId = question.id || question.detail_id;

        return {
          answer: this.userAnswers[index] || '',
          function: this.userExplanations[index] || '',
          score: scores[index] || 0,
          anatomy_detail_id: anatomyDetailId || null
        };
      }).filter(detail => detail.anatomy_detail_id !== null); // Remove details without valid IDs

      if (details.length === 0) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Tidak dapat menyimpan jawaban: ID pertanyaan tidak valid.'
        });
        return;
      }

      // Prepare the payload
      const payload = {
        user_id: userId,
        anatomy_id: this.anatomyId,
        skor: averageScore,
        details: details
      };

      // Submit to API
      const response = await this.userService.addAnatomyAnswer(payload);      if (response && response.status) {
        // Mark as submitted
        if (!this.completionData) {
          this.completionData = {};
        }
        this.completionData.submittedToServer = true;
        this.completionData.serverResponse = response;

        this.messageService.add({
          severity: 'success',
          summary: 'Berhasil',
          detail: 'Jawaban berhasil disimpan ke server!'
        });
      } else {
        throw new Error('Server response indicates failure');
      }

    } catch (error) {
      console.error('Error submitting anatomy answers:', error);
      this.messageService.add({
        severity: 'warn',
        summary: 'Peringatan',
        detail: 'Gagal menyimpan jawaban ke server. Data tetap tersimpan secara lokal.'
      });
    }
  }

  goBack() {
    this.router.navigate(['/app/osce/anatomi']);
  }

  getImageSrc(base64Image: string): string | null {
    if (!base64Image) return null;

    if (base64Image.startsWith('data:')) {
      return base64Image;
    }

    if (base64Image.startsWith('http')) {
      return base64Image;
    }

    if (this.isPdfData(base64Image)) {
      return null;
    }

    return `data:image/jpeg;base64,${base64Image}`;
  }  getPdfSrc(pdfData: string): string {
    if (!pdfData) {
      return '';
    }

    if (pdfData.startsWith('data:')) {
      return pdfData;
    }

    const result = `data:application/pdf;base64,${pdfData}`;
    return result;
  }  getPdfPreview(pdfData: string): string | null {
    if (!pdfData) {
      return null;
    }

    // Check if it's already a data URL
    if (pdfData.startsWith('data:')) {
      return pdfData;
    }

    // Check if it starts with %PDF (raw PDF content)
    if (pdfData.startsWith('%PDF')) {
      try {
        const base64 = btoa(pdfData);
        return `data:application/pdf;base64,${base64}`;
      } catch (error) {
        return null;
      }
    }

    // For explanation field, assume it's base64 PDF data
    try {
      const result = `data:application/pdf;base64,${pdfData}`;
      return result;
    } catch (error) {
      return null;
    }
  }

  isPdfData(base64String: string): boolean {
    if (!base64String) return false;

    try {
      // Remove data URL prefix if present
      const cleanBase64 = base64String.replace(/^data:application\/pdf;base64,/, '');

      // Try to decode the base64 string
      const binaryString = atob(cleanBase64);

      // Check for PDF signature
      const isPdf = binaryString.startsWith('%PDF');
      return isPdf;
    } catch (error) {
      // If base64 decoding fails, check if it looks like base64

      // Basic check if it looks like base64
      const base64Pattern = /^[A-Za-z0-9+/]*={0,2}$/;
      const looksLikeBase64 = base64Pattern.test(base64String) && base64String.length > 100;

      return looksLikeBase64;
    }
  }

  viewImage(imageSrc: string) {
    this.selectedImage = imageSrc;
    this.showImageDialog = true;
  }

  viewPdf(pdfData: string) {
    try {
      this.selectedPdf = this.getPdfSrc(pdfData);
      this.showPdfDialog = true;
    } catch (error) {
      console.error('Error viewing PDF:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Gagal menampilkan PDF - Format tidak valid'
      });
    }
  }

  closePdfDialog() {
    this.selectedPdf = null;
    this.showPdfDialog = false;
  }

  onPdfLoadComplete(event: any) {
    // PDF loaded successfully
  }

  onPdfError(error: any) {
    this.messageService.add({
      severity: 'error',
      summary: 'Error PDF',
      detail: 'Gagal memuat file PDF dari explanation field - periksa format data'
    });
  }

  // Utility for template
  Math = Math;

  getProgressPercentage(): number {
    if (this.currentQuestions.length === 0) {
      return 0;
    }

    // If learning is finished, show 100%
    if (this.isLearningFinished || this.isCompleted) {
      return 100;
    }

    // Calculate progress based on current question
    // For n questions: question 1 = (1/n)*100, question 2 = (2/n)*100, etc.
    const progress = Math.round(((this.currentQuestionIndex + 1) / this.currentQuestions.length) * 100);
    return progress;
  }

  getUserId(): string | null {
    try {
      const userData = localStorage.getItem('user');
      if (!userData) {
        return null;
      }

      const user = JSON.parse(userData);
      return user.ID_USER || null;
    } catch (error) {
      console.error('Error getting user ID:', error);
      return null;
    }
  }
}
